import * as React from 'react';
import classnames from 'classnames';
import { Text, Tag, Kebab, Info, StatusIndicator } from 'ui';
import { prefixClassName } from '../_conf';
import { ensureClaims } from 'framework/authorization';
import CheckboxPrimitive from '../FormComponents/Primitives/CheckboxPrimitive';
import './_styles.scss';
import { DataCardField } from './DataCard.interfaces';
import { GridAction, GridActionResult } from 'types';
import LinkPrimitive from '../Primitives/LinkPrimitive';
import { Statuses } from '../Status';

const clx = prefixClassName('data-card');

interface DataCardProps<T = any> {
  item: T;
  fields: DataCardField[];
  actions?: GridAction<T, GridActionResult>[];
  onActionInvoked?: (action: any, item: T) => void;
  showActions?: boolean;
  loadingItems?: T[];
  canSelectItems?: boolean;
  isSelected?: boolean;
  onToggleSelection?: (item: T) => void;
  isLoading?: boolean;
  disabled?: boolean;
  className?: string;
  index?: number;
}

export const DataCard = <T extends {}>({
  item,
  fields,
  actions,
  onActionInvoked,
  showActions = true,
  loadingItems = [],
  canSelectItems = false,
  isSelected = false,
  onToggleSelection,
  isLoading = false,
  disabled = false,
  className,
  index,
}: DataCardProps<T>) => {
  const isItemLoading = loadingItems.includes(item);

  const handleSelectionToggle = () => {
    if (onToggleSelection) {
      onToggleSelection(item);
    }
  };

  const renderField = (field: DataCardField) => {
    const handleClick = () => {
      if (field.onClick) {
        field.onClick();
      }
    };

    switch (field.type) {
      case 'status':
        return (
          <span className={`${clx}__status ${clx}__status--${field.value?.status?.toLowerCase()}`}>
            {field.value.label}
          </span>
        );
      case 'component': 
        return <div className={`${clx}__component`}><field.value /></div>
      case 'status-indicator':
        return <StatusIndicator caption={field.value.caption as Statuses} className={`${clx}__status-indicator`} />;
      case 'link':
        return (
          <LinkPrimitive to={field.value.link} className={`${clx}__link ${canSelectItems && `mrg-left-10`}`} onClick={handleClick}>
            {field.value.text}
          </LinkPrimitive>
        );
      case 'text':
        return <Text caption={field.value} className={`${clx}__text`} />;
      case 'tag':
        return <Tag value={field.value.tagValue} caption={field.value.caption} className={`${clx}__tag`} />;
      case 'tooltip':
        return (
          <Info
            caption={field.value.tooltipCaption}
            className={`${clx}__tooltip`}
            tooltipBody={
              field.value.tooltipBody && <div style={{ whiteSpace: 'pre-line' }}>{field.value.tooltipBody}</div>
            }
            toolTipPosition={field.value.tooltipPosition}
          />
        );
      case 'image':
        return <img src={field.value.imagePath} alt={field.value.alt} className={`${clx}__image`} />;
      default:
        return null;
    }
  };

  const authorizedActions = actions?.filter((a) => ensureClaims(a.authorize) && !a.hideRowAction) || [];

  return (
    <div
      className={classnames(clx, className, {
        [`${clx}--selected`]: isSelected,
        [`${clx}--loading`]: isLoading || isItemLoading,
      })}
    >
      <div className={`${clx}__content`}>
        {canSelectItems && (
          <CheckboxPrimitive
            className={`${clx}__checkbox`}
            value={isSelected}
            onChange={handleSelectionToggle}
            disabled={isLoading || isItemLoading || disabled}
            name={`checkbox-${(item as any).id || index || 'item'}`}
          />
        )}
        {showActions && authorizedActions.length > 0 && (
          <div className={`${clx}__actions`}>
            <Kebab
              options={authorizedActions.map((a) => {
                const config = a.config(item);
                return {
                  ...config,
                  onClick: () => onActionInvoked?.(config, item),
                };
              })}
              keepDropdown
            />
          </div>
        )}

        {fields.map((field, fieldIndex) => {
          if (!field.value) return null;
          if (field.disabled) return null;
          return (
            <div
              key={`${field.type}-${fieldIndex}`}
              className={classnames(`${clx}__field`, field.className)}
              style={{ gridArea: field.gridArea, justifySelf: field.justifySelf }}
            >
              <div className={`${clx}__value`}>
                {field.label && (
                  <div className={`${clx}__label`}>
                    <Text caption={field.label} />:{' '}
                    {(field.tooltipCaption || field.tooltipBody) && (
                      <Info
                        className={`${clx}__label__tooltip`}
                        caption={field.tooltipCaption}
                        tooltipBody={
                          field.tooltipBody && <div style={{ whiteSpace: 'pre-line' }}>{field.tooltipBody}</div>
                        }
                        toolTipPosition={field.tooltipPosition}
                      />
                    )}
                  </div>
                )}
                {renderField(field)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
